import React, { useEffect, useState } from "react";
import { Box, Tabs, Tab, Typography, SelectChangeEvent } from "@mui/material";
import Button from "../../components/Button/Button"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import { openInvoices } from "../../activeScreenSlice"
import {
  selectGlobalRptFilters,
  selectGlobalRptFiltersWithDayjs,
  selectTabLocalFilters,
  setGlobalRptFilters as setGlobalRptFiltersAction,
  setTabLocalFilters as setTabLocalFiltersAction,
  GlobalRptFilters,
  TabLocalFilters,
} from "../../reportingSlice"
import InvoiceReportingCounter from '../../components/InvoiceCounter/InvoiceReportingCounter'
import SecondaryTabsNav from "../../components/SecondaryTabsNav/SecondaryTabsNav";
import ReportingGlobalFilters from "../../components/ReportingGlobalFilters/ReportingGlobalFilters";
import { BUlist, SuppliersList } from "../../components/ReportingGlobalFilters/dummyReportingGlobalFiltersData";
import { usePostReportingInvoiceOperations, useGetAllSuppliers, usePostInvoiceComplexityReport, usePostBusinessUnitDashboardReport, usePostSupplierDashboard, usePostTeamAndReviwerDashBoardRpt, useGetDashBoardInvoiceStatusList, useGetDashBoardRejectionReasonList, useGetDashBoardInvoiceTypeList } from '../../hooks/Reporting';
import { useGetAllOpco } from "../../hooks/OpCo"
import TimePeriodSelector from "../../components/TimePeriodSelector/TimePeriodSelector";
import { formatDateForBackend } from "../../util/formatDate";
import InvoiceOperationsFilters from "../../components/InvoiceOperations/InvoiceOperationsFilter";
import InvoiceComplexityTab from "../../components/InvoiceComplexityTab/InvoiceComplexityTab";
import BusinessUnitReport from "../../components/BusinessUnitReport/BusinessUnitReport";
import SupplierDashboard from "../../components/SupplierDashboard/SupplierDashboard";
import TeamsAndReviewDashBoard from "../../components/TeamsAndReviewDashBoard/TeamsAndReviewDashBoard";
import DownloadPdfButton from "../../components/DownloadHtml/DownloadHtml";
import { CircularProgress } from "@mui/material";
import dayjs from "dayjs";
type TimePeriod = "today" | "this-week" | "this-month" | "this-quarter"

export default function Reporting() {
  const dispatch = useAppDispatch()

  // Redux state
  const globalRptFilters = useAppSelector(selectGlobalRptFilters);
  const globalRptFiltersWithDayjs = useAppSelector(selectGlobalRptFiltersWithDayjs);
  const tabLocalFilters = useAppSelector(selectTabLocalFilters);

  const setGlobalRptFilters = (filters: GlobalRptFilters | ((prev: GlobalRptFilters) => GlobalRptFilters)) => {
    if (typeof filters === 'function') {
      dispatch(setGlobalRptFiltersAction(filters(globalRptFilters)));
    } else {
      dispatch(setGlobalRptFiltersAction(filters));
    }
  };

  const setTabLocalFilters = (data: TabLocalFilters | ((prev: TabLocalFilters) => TabLocalFilters)) => {
    if (typeof data === 'function') {
      dispatch(setTabLocalFiltersAction(data(tabLocalFilters)));
    } else {
      dispatch(setTabLocalFiltersAction(data));
    }
  };

  const [selectedTab, setSelectedTab] = useState<number>(0);

  const allOpcosQuery = useGetAllOpco();
  const allSuppliersInfo = useGetAllSuppliers();
  const { mutate: applyFilters, isPending } = usePostReportingInvoiceOperations();
  const { mutate: applyInvComplexityFilters, isPending: isPendingComplexity } = usePostInvoiceComplexityReport();
  const { mutate: applyBusinessUnitDashboardFilters, isPending: isBULoading } = usePostBusinessUnitDashboardReport();
  const { mutate: applySupplierDashboardFilters, isPending: isSupplierLoading } = usePostSupplierDashboard();
  const { mutate: applyTeamAndReviewFilters, isPending: isTeamsRevLoading } = usePostTeamAndReviwerDashBoardRpt();
  const { data: invoiceStatusListData } = useGetDashBoardInvoiceStatusList()
  const { data: invoiceRejectionListData } = useGetDashBoardRejectionReasonList()
  const { data: invoiceTypeListData } = useGetDashBoardInvoiceTypeList()



  const [tabData, setTabData] = useState({
    0: null, // Invoice Operations
    1: null, // Invoice Complexity
    2: null, // Suppliers
    3: null, // Business Unit
    4: null, // Team & Reviewer
  });
  const [tabLoading, setTabLoading] = useState({
    0: false,
    1: false,
  });
  const [tabError, setTabError] = useState({
    0: null,
    1: null,

  });


  const transformGlobalRptFilters = (globalRptFilters) => {
    let [invoice_received_start, invoice_received_end] = globalRptFilters.invoiceReceiveDate || [];
    let [invoice_generation_start, invoice_generation_end] = globalRptFilters.invoiceGenerationDate || [];

    invoice_received_start = formatDate(invoice_received_start);
    invoice_received_end = formatDate(invoice_received_end);
    invoice_generation_start = formatDate(invoice_generation_start);
    invoice_generation_end = formatDate(invoice_generation_end);

    return {
      business_unit: globalRptFilters?.business_unit,
      supplier: globalRptFilters?.supplier,
      invoice_received_start,
      invoice_received_end,
      invoice_generation_start,
      invoice_generation_end,
    };
  }
  const handleInvoiceOpsFilterClear = (tabIndex) => {
    setTabLocalFilters(prev => ({
      ...prev,
      [tabIndex]: {},
    }));
    applyActiveTabFilters(selectedTab, transformGlobalRptFilters(globalRptFilters), {});
  };

  const tabLabels = [
    "Invoice Operations",
    "Invoice Complexity",
    "Suppliers",
    "Business Unit",
    "Team & Reviewer Performance",
  ];

  const dataset = [
    { month: 'April', stp_rate: 50, touchless_rate: 25, passed_erp_touchless_rate: 45 },
    { month: 'May', stp_rate: 25, touchless_rate: 45, passed_erp_touchless_rate: 12 },
    { month: 'June', stp_rate: 59, touchless_rate: 65, passed_erp_touchless_rate: 76 },
    { month: 'August', stp_rate: 90, touchless_rate: 18, passed_erp_touchless_rate: 15 }
  ]
  const seriesByPendingInv = [
    { dataKey: 'processing', stack: 'status', label: 'Processing', color: 'rgba(63, 81, 181, 0.1)' },
  ]

  const returnTotalCount = ((arr) => {
    return arr.reduce((acc, val) => acc + val, 0);
  });


  const applyActiveTabFilters = (tabIndex: number, newGlobalFilters: any = globalRptFilters, newLocalFilters: any = tabLocalFilters[tabIndex]) => {
    // Compose the payload
    const payload = {
      global_filters: newGlobalFilters,
      ...newLocalFilters,
    };

    // Call the correct API for the active tab
    switch (tabIndex) {
      case 0:
        applyFilters(payload, {
          onSuccess: (data) => setTabData((prev) => ({ ...prev, 0: data })),
        });

        break;
      case 1:
        applyInvComplexityFilters(payload, {
          onSuccess: (data) => setTabData((prev) => ({ ...prev, 1: data })),
        })
        break;
      case 2:
        applySupplierDashboardFilters(payload, {
          onSuccess: (data) => setTabData((prev) => ({ ...prev, 2: data })),
        })
        break;
      case 3:
        applyBusinessUnitDashboardFilters(payload, {
          onSuccess: (data) => setTabData((prev) => ({ ...prev, 3: data })),
        })
        break;
      case 4:
        applyTeamAndReviewFilters(payload, {
          onSuccess: (data) => setTabData((prev) => ({ ...prev, 4: data })),
        })
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    const transformedGlobalFilters = transformGlobalRptFilters(globalRptFilters)
    const payload = {global_filters:transformedGlobalFilters, ...tabLocalFilters[selectedTab] };
    if (selectedTab === 0) {
      applyFilters(payload, {
        onSuccess: (data) => setTabData((prev) => ({ ...prev, 0: data })),
      });
    } else if (selectedTab === 1) {
      applyInvComplexityFilters(payload, {
        onSuccess: (data) => setTabData((prev) => ({ ...prev, 1: data })),
      });
    } else if (selectedTab === 2) {
      applySupplierDashboardFilters(payload, {
        onSuccess: (data) => setTabData((prev) => ({ ...prev, 2: data })),
      })
    } else if (selectedTab === 3) {
      applyBusinessUnitDashboardFilters(payload, {
        onSuccess: (data) => setTabData((prev) => ({ ...prev, 3: data })),
      })
    } else if (selectedTab === 4) {
      applyTeamAndReviewFilters(payload, {
        onSuccess: (data) => setTabData((prev) => ({ ...prev, 4: data })),
      })
    }
  }, [selectedTab]);

  const currentTabData = tabData[selectedTab];

  const formatDate = (date) => {
    if (!date) return null;
    // Use your util if available, else fallback
    if (typeof formatDateForBackend === "function") {
      return dayjs(date).format("YYYY-MM-DD");
    }
    return new Date(date).toISOString().slice(0, 10);
  };

  const handleGlobalRptFilters = (globalRptFilters) => {
    const transformedGlobalFilters = transformGlobalRptFilters(globalRptFilters)
    applyActiveTabFilters(selectedTab, transformedGlobalFilters, tabLocalFilters[selectedTab]);
  };

  //handle local filters here 
  const handleTabLocalFilters = (tabIndex, newLocalFilters) => {
    setTabLocalFilters(prev => ({
      ...prev,
      [tabIndex]: newLocalFilters,
    }));
     const transformedGlobalFilters = transformGlobalRptFilters(globalRptFilters)
    applyActiveTabFilters(tabIndex, transformedGlobalFilters, newLocalFilters);
  };

  // Your tab contents as an array or components
  const tabContents = [
    <Box sx={{ padding: "32px", borderRadius: "var(--Radius-12)", background: 'var(--background-off-white)' }}>
      <InvoiceOperationsFilters
        data={currentTabData}
        isPending={isPending}
        onApplyInvOpsRptFilter={(localFilters) => handleTabLocalFilters(0, localFilters)}
        onClear={() => handleInvoiceOpsFilterClear(0)}
        initialFilters={tabLocalFilters[0]}
        invoiceStatusListData={invoiceStatusListData}
        invoiceRejectionListData={invoiceRejectionListData}
        invoiceTypeListData={invoiceTypeListData}
      />

    </Box>,

    <Box sx={{ padding: "32px", background: 'var(--background-off-white)', borderRadius: "12px" }}>

      <InvoiceComplexityTab
        data={currentTabData}
        onApplyInvComplexRptFilter={(localFilters) => handleTabLocalFilters(1, localFilters)}
        onClear={() => handleInvoiceOpsFilterClear(1)}
        initialFilters={tabLocalFilters[1]}
        invoiceStatusListData={invoiceStatusListData}
        isPending={isPendingComplexity}
      />
    </Box>,
    <div>
      <SupplierDashboard
        data={currentTabData}
        onReportFilterApply={(localFilters) => handleTabLocalFilters(2, localFilters)}
        onClear={() => handleInvoiceOpsFilterClear(2)}
        initialFilters={tabLocalFilters[2]}
        invoiceStatusListData={invoiceStatusListData}
        invoiceRejectionListData={invoiceRejectionListData}
        isPending={isSupplierLoading}
      /></div>,
    <Box sx={{ padding: "32px", background: 'var(--background-off-white)', borderRadius: "12px" }}>
      <BusinessUnitReport
        data={currentTabData}
        onBuReportFilterApply={(localFilters) => handleTabLocalFilters(3, localFilters)}
        onClear={() => handleInvoiceOpsFilterClear(3)}
        initialFilters={tabLocalFilters[3]}
        invoiceStatusListData={invoiceStatusListData}
        invoiceRejectionListData={invoiceRejectionListData}
        isPending={isBULoading}
      />
    </Box>,
    <Box sx={{ padding: "32px", background: 'var(--background-off-white)', borderRadius: "12px" }}>
      <TeamsAndReviewDashBoard
        data={currentTabData}
        onBuReportFilterApply={(localFilters) => handleTabLocalFilters(4, localFilters)}
        onClear={() => handleInvoiceOpsFilterClear(4)}
        initialFilters={tabLocalFilters[4]}
        invoiceStatusListData={invoiceStatusListData}
        isPending={isTeamsRevLoading}
      />
    </Box>,
  ];

  const handleFilterChange = (key, value) => {
    setGlobalRptFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleClear = () => {
    const clearedFilters: GlobalRptFilters = {
      business_unit: [],
      supplier: [],
      invoice_received_start: null,
      invoice_received_end: null,
      invoice_generation_start: null,
      invoice_generation_end: null,
      invoiceReceiveDate: null,
      invoiceGenerationDate: null,
    };
    setGlobalRptFilters(clearedFilters);
    applyActiveTabFilters(selectedTab, transformGlobalRptFilters(clearedFilters), tabLocalFilters[selectedTab]);
  };


  function getCounterPropsForTab(tabIndex, data) {
    // Provide default values for all required props
    const defaultProps = {
      invoice_count: 0,
      stpRate: '',
      touchlessInvoiceRate: '',
      passedERPtouchlessRate: '',
      pendingInvoices: 0,
      pendingOutOfSLA: 0,
    };

    if (!data) return defaultProps;

    return {
      invoice_count: data?.invoice_report?.total_invoices ?? 0,
      stpRate: data?.invoice_report?.stp_rate ?? '0',
      touchlessInvoiceRate: data?.invoice_report?.touchless_rate ?? '0',
      passedERPtouchlessRate: data?.invoice_report?.passed_erp_touchless_rate ?? '0',
      pendingInvoices: data?.invoice_report?.pending_invoices ?? 0,
      pendingOutOfSLA: data?.invoice_report?.pending_out_of_sla ?? 0,
    };
  }

  return (
    <Box id="reporting-root">
      <Box sx={{ padding: '24px 32px 16px 32px', display: 'flex' }}>
        <Typography variant="h1" sx={{ fontSize: '24px', }}>Reporting dashboard</Typography>
        <DownloadPdfButton />
      </Box>

      <ReportingGlobalFilters
        BUlist={BUlist}
        SuppliersList={SuppliersList}
        globalRptFilters={globalRptFiltersWithDayjs}
        onChange={(key, value) => handleFilterChange(key, value)}
        handleClear={handleClear}
        handleGlobalRptFilters={() => handleGlobalRptFilters(globalRptFilters)}
      />
      {isPending ? <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}><CircularProgress /></Box> : <InvoiceReportingCounter {...getCounterPropsForTab(selectedTab, currentTabData)} />}

      <SecondaryTabsNav
        tabs={tabLabels}
        value={selectedTab}
        onChange={(_, newValue) => setSelectedTab(newValue)}
      />
      <Box sx={{ padding: "16px 32px 32px 32px", background: 'var(--white)' }}>
        {tabContents[selectedTab]}
      </Box>
    </Box>
  )
}
